import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  ClipboardList, 
  Users, 
  Package, 
  Check, 
  X,
  ArrowRight,
  Mail,
  Building2,
  User,
  Star,
  ChevronRight,
  Sparkles,
  BarChart3,
  QrCode,
  FileText,
  Calendar,
  Bell,
  Loader2
} from "lucide-react";
import { CirculateLogo } from "@/components/CirculateLogo";

// Brand colors from Circulate logo
const colors = {
  red: "#C4453A",
  orange: "#E67B44", 
  gold: "#E5A94B",
  green: "#3A8D6E",
  blue: "#4B8BC7",
  purple: "#6B4E8D",
  brown: "#5C3D2E",
  cream: "#F5F1E8"
};

export default function Landing() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    message: ""
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, formData);
      setFormSubmitted(true);
    } catch (error) {
      console.error("Contact form error:", error);
      setSubmitError("Something went wrong. Please try again or email design@sarahswingle.com directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Rainbow stripe at top */}
      <div className="fixed top-0 left-0 right-0 h-1 brand-gradient z-[60]" />
      
      {/* Navigation */}
      <nav 
        className="fixed top-1 left-0 right-0 z-50 border-b"
        style={{ 
          backgroundColor: "rgba(245,241,232,0.9)", 
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          borderColor: "rgba(0,0,0,0.05)"
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <CirculateLogo size={40} showText={true} />
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm hover:opacity-70 transition-opacity" style={{ color: colors.brown }}>Features</a>
            <a href="#pricing" className="text-sm hover:opacity-70 transition-opacity" style={{ color: colors.brown }}>Pricing</a>
            <a href="#contact" className="text-sm hover:opacity-70 transition-opacity" style={{ color: colors.brown }}>Contact</a>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button 
                variant="ghost" 
                className="rounded-full px-5"
                style={{ color: colors.brown }}
                data-testid="nav-login"
              >
                Log in
              </Button>
            </Link>
            <Link to="/register">
              <Button 
                className="rounded-full px-5"
                style={{ backgroundColor: colors.purple, color: "#FDFDF9" }}
                data-testid="nav-get-started"
              >
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            {/* Left Content - Text */}
            <div className="md:col-span-7">
              <div 
                className="uppercase text-xs font-bold tracking-[0.2em] mb-6"
                style={{ color: "#6D597A" }}
              >
                Library Management Reimagined
              </div>
              
              <h1 
                className="text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-none mb-8"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: "#1A1A1A" }}
              >
                Streamline your library operations
              </h1>
              
              <p 
                className="text-lg md:text-xl leading-relaxed mb-10 max-w-xl"
                style={{ color: "#5C5C5C" }}
              >
                The all-in-one platform for project management, staff evaluation, and inventory tracking. 
                Built specifically for modern libraries.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button 
                    size="lg"
                    className="rounded-full px-8 py-6 text-base group"
                    style={{ backgroundColor: "#1A1A1A", color: "#FDFDF9" }}
                    data-testid="cta-get-started"
                  >
                    Start Free Trial
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href="#contact">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="rounded-full px-8 py-6 text-base"
                    style={{ borderColor: "#1A1A1A", color: "#1A1A1A" }}
                    data-testid="cta-request-demo"
                  >
                    Request Demo
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Content - Hero Image */}
            <div className="md:col-span-5">
              <div 
                className="relative rounded-3xl overflow-hidden"
                style={{ 
                  boxShadow: "0 32px 64px rgba(0,0,0,0.08)",
                  aspectRatio: "4/5"
                }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_89042889-3a6d-4693-b050-7fd3b764ddc6/artifacts/wxfvg785_Untitled%20-%20June%2023%2C%202026%20at%2009.14.44.JPEG"
                  alt="Modern library interior"
                  className="w-full h-full object-cover"
                />
                <div 
                  className="absolute inset-0"
                  style={{ background: "linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 50%)" }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section id="features" className="py-24 px-6" style={{ backgroundColor: "#F3F4F1" }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div 
              className="uppercase text-xs font-bold tracking-[0.2em] mb-4"
              style={{ color: "#6D597A" }}
            >
              Features
            </div>
            <h2 
              className="text-4xl md:text-5xl font-light tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "#1A1A1A" }}
            >
              Everything you need to run<br />a modern library
            </h2>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Project Management - Large Card */}
            <div 
              className="md:col-span-8 p-8 md:p-12 rounded-3xl border"
              style={{ 
                backgroundColor: colors.blue, 
                borderColor: "rgba(0,0,0,0.1)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.04)"
              }}
              data-testid="feature-project-management"
            >
              <div className="flex items-start justify-between mb-8">
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: "rgba(255,255,255,0.8)" }}
                >
                  <ClipboardList className="w-7 h-7" style={{ color: "#1A1A1A" }} />
                </div>
                <span 
                  className="px-4 py-1.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: "rgba(255,255,255,0.8)", color: "#1A1A1A" }}
                >
                  Core Feature
                </span>
              </div>
              <h3 
                className="text-2xl md:text-3xl font-medium mb-4"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: "#1A1A1A" }}
              >
                Project Management
              </h3>
              <p className="text-base leading-relaxed mb-6" style={{ color: "#5C5C5C" }}>
                Submit program proposals, track events, and manage recurring programs with ease. 
                Built-in approval workflows keep supervisors in the loop.
              </p>
              <div className="flex flex-wrap gap-3">
                {["Program Proposals", "Event Tracking", "Recurring Schedules", "Approval Workflows"].map((tag) => (
                  <span 
                    key={tag}
                    className="px-3 py-1.5 rounded-full text-xs font-medium"
                    style={{ backgroundColor: "rgba(255,255,255,0.6)", color: "#1A1A1A" }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Quick Stats Card */}
            <div 
              className="md:col-span-4 p-8 rounded-3xl border flex flex-col justify-between"
              style={{ 
                backgroundColor: colors.purple, 
                borderColor: "rgba(0,0,0,0.1)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.04)"
              }}
            >
              <div>
                <BarChart3 className="w-8 h-8 mb-4" style={{ color: "#1A1A1A" }} />
                <h4 className="text-lg font-medium mb-2" style={{ color: "#1A1A1A" }}>Real-time Analytics</h4>
                <p className="text-sm" style={{ color: "#5C5C5C" }}>Track staff productivity and program success at a glance.</p>
              </div>
              <div className="mt-6 pt-6 border-t" style={{ borderColor: "rgba(0,0,0,0.1)" }}>
                <div className="text-3xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>87%</div>
                <div className="text-xs" style={{ color: "#5C5C5C" }}>Avg. productivity increase</div>
              </div>
            </div>

            {/* Employee Evaluation Card */}
            <div 
              className="md:col-span-5 p-8 md:p-10 rounded-3xl border"
              style={{ 
                backgroundColor: colors.yellow, 
                borderColor: "rgba(0,0,0,0.1)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.04)"
              }}
              data-testid="feature-employee-evaluation"
            >
              <div 
                className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6"
                style={{ backgroundColor: "rgba(255,255,255,0.8)" }}
              >
                <Users className="w-6 h-6" style={{ color: "#1A1A1A" }} />
              </div>
              <h3 
                className="text-xl md:text-2xl font-medium mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: "#1A1A1A" }}
              >
                Staff Productivity & Evaluation
              </h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: "#5C5C5C" }}>
                AI-generated monthly reports, goal tracking, and shout-outs for team recognition.
              </p>
              <div className="flex items-center gap-2 text-sm" style={{ color: "#1A1A1A" }}>
                <FileText className="w-4 h-4" />
                <span>Auto-generated .docx reports</span>
              </div>
            </div>

            {/* Inventory Card */}
            <div 
              className="md:col-span-7 p-8 md:p-10 rounded-3xl border"
              style={{ 
                backgroundColor: colors.green, 
                borderColor: "rgba(0,0,0,0.1)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.04)"
              }}
              data-testid="feature-inventory"
            >
              <div className="flex items-start justify-between mb-6">
                <div 
                  className="w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: "rgba(255,255,255,0.8)" }}
                >
                  <Package className="w-6 h-6" style={{ color: "#1A1A1A" }} />
                </div>
              </div>
              <h3 
                className="text-xl md:text-2xl font-medium mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: "#1A1A1A" }}
              >
                Smart Inventory Management
              </h3>
              <p className="text-sm leading-relaxed mb-6" style={{ color: "#5C5C5C" }}>
                QR-code tracking, hold system with auto-expiry, minimum stock alerts, and bulk CSV uploads.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm" style={{ color: "#1A1A1A" }}>
                  <QrCode className="w-4 h-4" />
                  <span>QR Scanning</span>
                </div>
                <div className="flex items-center gap-2 text-sm" style={{ color: "#1A1A1A" }}>
                  <Bell className="w-4 h-4" />
                  <span>Stock Alerts</span>
                </div>
                <div className="flex items-center gap-2 text-sm" style={{ color: "#1A1A1A" }}>
                  <Calendar className="w-4 h-4" />
                  <span>5-Day Hold Expiry</span>
                </div>
                <div className="flex items-center gap-2 text-sm" style={{ color: "#1A1A1A" }}>
                  <Sparkles className="w-4 h-4" />
                  <span>Quick Math Tools</span>
                </div>
              </div>
            </div>

            {/* AI Assistant Mini Card */}
            <div 
              className="md:col-span-4 p-6 rounded-3xl border"
              style={{ 
                backgroundColor: colors.red, 
                borderColor: "rgba(0,0,0,0.1)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.04)"
              }}
            >
              <Sparkles className="w-6 h-6 mb-3" style={{ color: "#1A1A1A" }} />
              <h4 className="text-base font-medium mb-1" style={{ color: "#1A1A1A" }}>AI Assistant</h4>
              <p className="text-xs" style={{ color: "#5C5C5C" }}>Generate summaries and reports with Gemini AI</p>
            </div>

            {/* Mobile Friendly Mini Card */}
            <div 
              className="md:col-span-4 p-6 rounded-3xl border"
              style={{ 
                backgroundColor: colors.orange, 
                borderColor: "rgba(0,0,0,0.1)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.04)"
              }}
            >
              <svg className="w-6 h-6 mb-3" viewBox="0 0 24 24" fill="none" stroke="#1A1A1A" strokeWidth="1.5">
                <rect x="5" y="2" width="14" height="20" rx="2" />
                <line x1="12" y1="18" x2="12" y2="18.01" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <h4 className="text-base font-medium mb-1" style={{ color: "#1A1A1A" }}>Mobile Optimized</h4>
              <p className="text-xs" style={{ color: "#5C5C5C" }}>Simplified view for quick adds and scanning</p>
            </div>

            {/* Collaboration Mini Card */}
            <div 
              className="md:col-span-4 p-6 rounded-3xl border"
              style={{ 
                backgroundColor: "white", 
                borderColor: "rgba(0,0,0,0.1)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.04)"
              }}
            >
              <Users className="w-6 h-6 mb-3" style={{ color: "#1A1A1A" }} />
              <h4 className="text-base font-medium mb-1" style={{ color: "#1A1A1A" }}>Team Collaboration</h4>
              <p className="text-xs" style={{ color: "#5C5C5C" }}>Share entries, track contributions, celebrate wins</p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-24 px-6" style={{ backgroundColor: "#FDFDF9" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div 
              className="uppercase text-xs font-bold tracking-[0.2em] mb-4"
              style={{ color: "#6D597A" }}
            >
              Comparison
            </div>
            <h2 
              className="text-4xl md:text-5xl font-light tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "#1A1A1A" }}
            >
              Why choose Circulate?
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="comparison-table">
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(0,0,0,0.1)" }}>
                  <th className="text-left py-4 px-4 font-medium" style={{ color: "#1A1A1A" }}>Feature</th>
                  <th className="text-center py-4 px-4 font-medium" style={{ color: "#1A1A1A" }}>
                    <span className="inline-flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Circulate
                    </span>
                  </th>
                  <th className="text-center py-4 px-4 font-medium" style={{ color: "#5C5C5C" }}>Spreadsheets</th>
                  <th className="text-center py-4 px-4 font-medium" style={{ color: "#5C5C5C" }}>Generic PM Tools</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: "Built for Libraries", circulate: true, sheets: false, generic: false },
                  { feature: "AI Monthly Reports", circulate: true, sheets: false, generic: false },
                  { feature: "QR Inventory Tracking", circulate: true, sheets: false, generic: false },
                  { feature: "Proposal Approval Workflow", circulate: true, sheets: false, generic: true },
                  { feature: "Staff Goal Tracking", circulate: true, sheets: false, generic: true },
                  { feature: "Mobile Quick-Add", circulate: true, sheets: true, generic: true },
                  { feature: "Supervisor Dashboard", circulate: true, sheets: false, generic: true },
                  { feature: "Team Recognition (Shout-outs)", circulate: true, sheets: false, generic: false },
                  { feature: "Barcode Scanner", circulate: true, sheets: false, generic: false },
                  { feature: "Auto-Expiring Holds", circulate: true, sheets: false, generic: false },
                ].map((row, i) => (
                  <tr 
                    key={row.feature}
                    style={{ borderBottom: "1px solid rgba(0,0,0,0.05)" }}
                  >
                    <td className="py-4 px-4 text-sm" style={{ color: "#1A1A1A" }}>{row.feature}</td>
                    <td className="py-4 px-4 text-center">
                      {row.circulate ? (
                        <Check className="w-5 h-5 mx-auto" style={{ color: "#22c55e" }} />
                      ) : (
                        <X className="w-5 h-5 mx-auto" style={{ color: "#ef4444" }} />
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {row.sheets ? (
                        <Check className="w-5 h-5 mx-auto" style={{ color: "#22c55e" }} />
                      ) : (
                        <X className="w-5 h-5 mx-auto" style={{ color: "#d1d5db" }} />
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {row.generic ? (
                        <Check className="w-5 h-5 mx-auto" style={{ color: "#22c55e" }} />
                      ) : (
                        <X className="w-5 h-5 mx-auto" style={{ color: "#d1d5db" }} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pricing Section - Donation Based */}
      <section id="pricing" className="py-24 px-6" style={{ backgroundColor: "#F3F4F1" }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div 
              className="uppercase text-xs font-bold tracking-[0.2em] mb-4"
              style={{ color: "#6D597A" }}
            >
              Support the Project
            </div>
            <h2 
              className="text-4xl md:text-5xl font-light tracking-tight mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "#1A1A1A" }}
            >
              Free for all libraries
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: "#5C5C5C" }}>
              Circulate is built by librarians, for librarians. We believe every library deserves 
              modern tools regardless of budget. Use it free, donate if you can.
            </p>
          </div>

          {/* Donation Card */}
          <div 
            className="p-8 md:p-12 rounded-3xl border text-center"
            style={{ 
              background: `linear-gradient(135deg, ${colors.blue} 0%, ${colors.purple} 100%)`,
              borderColor: "rgba(0,0,0,0.1)", 
              boxShadow: "0 16px 48px rgba(0,0,0,0.1)" 
            }}
            data-testid="pricing-donation"
          >
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{ backgroundColor: "rgba(255,255,255,0.9)", color: "#1A1A1A" }}
            >
              <Sparkles className="w-4 h-4" />
              100% Free to Use
            </div>
            
            <h3 
              className="text-3xl md:text-4xl font-medium mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "#1A1A1A" }}
            >
              Pay What You Can
            </h3>
            
            <p className="text-base leading-relaxed mb-8 max-w-xl mx-auto" style={{ color: "#5C5C5C" }}>
              All features included. No limits, no premium tiers. If Circulate helps your library, 
              consider supporting its development with a donation.
            </p>

            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {["Program Proposals", "Staff Reports", "Inventory Management", "AI Summaries", "Mobile App", "Offline Checkout"].map((tag) => (
                <span 
                  key={tag}
                  className="px-4 py-2 rounded-full text-sm"
                  style={{ backgroundColor: "rgba(255,255,255,0.6)", color: "#1A1A1A" }}
                >
                  <Check className="w-4 h-4 inline mr-1" style={{ color: "#22c55e" }} />
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button 
                  size="lg"
                  className="rounded-full px-8 group"
                  style={{ backgroundColor: "#1A1A1A", color: "#FDFDF9" }}
                >
                  Get Started Free
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a href="#contact">
                <Button 
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                  style={{ backgroundColor: "rgba(255,255,255,0.8)", borderColor: "transparent", color: "#1A1A1A" }}
                >
                  <Star className="w-4 h-4 mr-2" />
                  Make a Donation
                </Button>
              </a>
            </div>
          </div>

          {/* Why Donation Based */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <div 
                className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: colors.green }}
              >
                <Users className="w-6 h-6" style={{ color: "#1A1A1A" }} />
              </div>
              <h4 className="font-medium mb-2" style={{ color: "#1A1A1A" }}>Built for Community</h4>
              <p className="text-sm" style={{ color: "#5C5C5C" }}>
                Libraries serve everyone. So should the tools that power them.
              </p>
            </div>
            <div className="text-center p-6">
              <div 
                className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: colors.orange }}
              >
                <Package className="w-6 h-6" style={{ color: "#1A1A1A" }} />
              </div>
              <h4 className="font-medium mb-2" style={{ color: "#1A1A1A" }}>No Feature Limits</h4>
              <p className="text-sm" style={{ color: "#5C5C5C" }}>
                Every library gets every feature. No artificial restrictions.
              </p>
            </div>
            <div className="text-center p-6">
              <div 
                className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: colors.purple }}
              >
                <Star className="w-6 h-6" style={{ color: "#1A1A1A" }} />
              </div>
              <h4 className="font-medium mb-2" style={{ color: "#1A1A1A" }}>Donations Welcome</h4>
              <p className="text-sm" style={{ color: "#5C5C5C" }}>
                Your support helps us improve Circulate for all libraries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact / Demo Request Form */}
      <section id="contact" className="py-24 px-6" style={{ backgroundColor: "#F3F4F1" }}>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div 
              className="uppercase text-xs font-bold tracking-[0.2em] mb-4"
              style={{ color: "#6D597A" }}
            >
              Get in Touch
            </div>
            <h2 
              className="text-4xl md:text-5xl font-light tracking-tight mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "#1A1A1A" }}
            >
              Request a Demo
            </h2>
            <p className="text-lg" style={{ color: "#5C5C5C" }}>
              See Circulate in action. We'll walk you through every feature.
            </p>
          </div>

          {formSubmitted ? (
            <div 
              className="p-12 rounded-3xl border text-center bg-white"
              style={{ borderColor: "rgba(0,0,0,0.1)" }}
              data-testid="form-success"
            >
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: colors.green }}
              >
                <Check className="w-8 h-8" style={{ color: "#22c55e" }} />
              </div>
              <h3 
                className="text-2xl font-medium mb-2"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: "#1A1A1A" }}
              >
                Thank you!
              </h3>
              <p style={{ color: "#5C5C5C" }}>
                We've received your request and will be in touch within 24 hours at the email you provided.
              </p>
              <p className="mt-2 text-sm" style={{ color: "#6D597A" }}>
                You can also reach us directly at <a href="mailto:design@sarahswingle.com" className="underline">design@sarahswingle.com</a>
              </p>
            </div>
          ) : (
            <form 
              onSubmit={handleSubmit}
              className="p-8 md:p-12 rounded-3xl border bg-white"
              style={{ borderColor: "rgba(0,0,0,0.1)", boxShadow: "0 8px 32px rgba(0,0,0,0.04)" }}
              data-testid="contact-form"
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: "#1A1A1A" }}>
                    <User className="w-4 h-4 inline mr-2" />
                    Your Name
                  </label>
                  <Input 
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Jane Smith"
                    required
                    className="rounded-xl"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: "#1A1A1A" }}>
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email Address
                  </label>
                  <Input 
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="jane@library.org"
                    required
                    className="rounded-xl"
                    data-testid="input-email"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{ color: "#1A1A1A" }}>
                  <Building2 className="w-4 h-4 inline mr-2" />
                  Organization
                </label>
                <Input 
                  type="text"
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  placeholder="Metro Public Library"
                  className="rounded-xl"
                  data-testid="input-organization"
                />
              </div>

              <div className="mb-8">
                <label className="block text-sm font-medium mb-2" style={{ color: "#1A1A1A" }}>
                  Message (Optional)
                </label>
                <Textarea 
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us about your library and what you're looking for..."
                  rows={4}
                  className="rounded-xl resize-none"
                  data-testid="input-message"
                />
              </div>

              {submitError && (
                <div className="mb-4 p-4 rounded-xl text-sm" style={{ backgroundColor: colors.red, color: "#1A1A1A" }}>
                  {submitError}
                </div>
              )}

              <Button 
                type="submit"
                size="lg"
                className="w-full rounded-full group"
                style={{ backgroundColor: "#1A1A1A", color: "#FDFDF9" }}
                data-testid="submit-demo-request"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Request Demo
                    <ChevronRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-24 px-6" style={{ backgroundColor: "#1A1A1A" }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#FDFDF9" }}>
                  <Package className="w-5 h-5" style={{ color: "#1A1A1A" }} />
                </div>
                <span className="text-2xl font-medium" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#FDFDF9" }}>
                  Circulate
                </span>
              </div>
              <p className="text-sm leading-relaxed max-w-md" style={{ color: "rgba(253,253,249,0.6)" }}>
                The modern library management platform. Built for librarians, by people who understand 
                the unique challenges of running library programs.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-medium mb-4" style={{ color: "#FDFDF9" }}>Product</h4>
              <ul className="space-y-3">
                {["Features", "Pricing", "Integrations", "Updates"].map((item) => (
                  <li key={item}>
                    <a 
                      href={`#${item.toLowerCase()}`}
                      className="text-sm hover:opacity-100 transition-opacity"
                      style={{ color: "rgba(253,253,249,0.6)" }}
                    >
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-medium mb-4" style={{ color: "#FDFDF9" }}>Company</h4>
              <ul className="space-y-3">
                {["About", "Blog", "Careers", "Contact"].map((item) => (
                  <li key={item}>
                    <a 
                      href={`#${item.toLowerCase()}`}
                      className="text-sm hover:opacity-100 transition-opacity"
                      style={{ color: "rgba(253,253,249,0.6)" }}
                    >
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div 
            className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4"
            style={{ borderTop: "1px solid rgba(253,253,249,0.1)" }}
          >
            <p className="text-xs" style={{ color: "rgba(253,253,249,0.4)" }}>
              © 2026 Circulate. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              {["Privacy Policy", "Terms of Service"].map((item) => (
                <a 
                  key={item}
                  href="#"
                  className="text-xs hover:opacity-100 transition-opacity"
                  style={{ color: "rgba(253,253,249,0.4)" }}
                >
                  {item}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
